import React from 'react';
import { TableStyles } from "@/components/ui";
import { Clock } from "lucide-react";
import { TableActions } from "@/components/ui/TableActions";
import ErrorBoundary from "@/components/ui/ErrorBoundary";

const getMemberName = (user) => {
  if (!user) return "—";
  if (typeof user === 'string') return user;
  return user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown';
};

const formatDateStr = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getStageDisplay = (status) => {
  if (status === 'pending_approval') {
    return {
      label: 'Pending Approval',
      colorClass: 'text-amber-700 bg-amber-50 border-amber-200'
    };
  }
  if (status === 'converted') {
    return {
      label: 'Converted',
      colorClass: 'text-emerald-700 bg-emerald-50 border-emerald-200'
    };
  }
  return {
    label: status || 'New',
    colorClass: 'text-slate-700 bg-white border-slate-200'
  };
};

export const getLeadExecutionColumns = ({ user, projectManagers, designers, onSiteExcChange, onView, onEdit, onAdd, onDisable }) => {
  const isAdmin = true;

  return [
    {
      header: "Verve ID",
      key: "verveId",
      minWidth: '130px',
      render: (row) => {
        const l = row.leadId || {};
        return (
          <div className="flex flex-col gap-1 items-start">
            <span className={TableStyles.idCell}>
              {l.customId || l.verveId || "—"}
            </span>
          </div>
        )
      }
    },
    {
      header: "Customer Info",
      key: "customerName",
      render: (row) => {
        const l = row.leadId || {};
        const fullName = l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name";
        const isReminderDue = l.isReminderDue;
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex flex-col min-w-0">
              <span className={`${TableStyles.primaryText} flex items-center gap-2 flex-wrap`}>
                {fullName}
                {isReminderDue && (
                  <div className="flex items-center gap-1 text-red-500 font-bold text-[9px] animate-pulse uppercase tracking-wider">
                    <Clock size={10} />
                    <span>Follow Up Due</span>
                  </div>
                )}
              </span>
              <span className={`${TableStyles.secondaryText} flex items-center gap-1`}>
                {l.phoneNumber || l.phone || "No Phone"}
                {l.email && <span className="mx-1 opacity-50">|</span>}
                {l.email && l.email.toLowerCase()}
              </span>
            </div>
          </div>
        );
      }
    },
    {
      header: "Address",
      key: "location",
      render: (row) => {
        const l = row.leadId || {};
        const addressParts = [
          l.addressLine1,
          l.addressLine2,
          l.city,
          l.district,
          l.state,
          l.pincode
        ].filter(Boolean);
        const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : "—";
        return (
          <div className="flex flex-col">
            <span className={`${TableStyles.regularText} max-w-[200px] line-clamp-2`} title={fullAddress}>
              {fullAddress}
            </span>
          </div>
        );
      }
    },
    {
      header: "Source",
      key: "source",
      minWidth: "100px",
      render: (row) => {
        const l = row.leadId || {};
        const sourceLabel = l.source === 'Bulk' ? 'Bulk Import' : (l.source || "Manual");
        return <span className={TableStyles.badge}>{sourceLabel}</span>;
      }
    },
    {
      header: "Reference Type",
      key: "referenceType",
      minWidth: "120px",
      render: (row) => {
        const l = row.leadId || {};
        return <span className={`${TableStyles.regularText} capitalize`}>{l.referenceType || "—"}</span>;
      }
    },
    {
      header: "Staff Name",
      key: "telecaller",
      minWidth: "130px",
      render: (row) => {
        const l = row.leadId || {};
        const staff = l.telecaller || l.createdBy;
        return staff ? (
          <div className="flex flex-col min-w-0">
            <div className="text-brand-dark text-[11px] truncate" title={getMemberName(staff)}>
              {getMemberName(staff)}
            </div>
            <div className="text-[10px] text-slate-500 whitespace-nowrap">
              {formatDateStr(l.updatedAt || l.createdAt)}
            </div>
          </div>
        ) : <span className="text-[11px] text-slate-400">Unassigned</span>;
      }
    },
    {
      header: "Site Exec",
      key: "siteExecutive",
      minWidth: "120px",
      render: (row) => {
        const l = row.leadId || {};
        const staff = l.siteExecutive;
        return staff ? (
          <div className="text-brand-dark text-[11px] truncate" title={getMemberName(staff)}>
            {getMemberName(staff)}
          </div>
        ) : <span className="text-[11px] text-slate-400">Unassigned</span>;
      }
    },
    {
      header: "Designer",
      key: "designer",
      minWidth: "120px",
      render: (row) => {
        const l = row.leadId || {};
        const staff = l.designer;
        return staff ? (
          <div className="text-brand-dark text-[11px] truncate" title={getMemberName(staff)}>
            {getMemberName(staff)}
          </div>
        ) : <span className="text-[11px] text-slate-400">Unassigned</span>;
      }
    },
    /* 
    {
      header: "Status",
      key: "teleStatus",
      minWidth: "140px",
      render: (row) => {
        const l = row.leadId || {};
        const displayStatus = l.status === "drop" ? "drop" : (l.teleStatus || l.status);
        const { label, colorClass } = getStageDisplay(displayStatus);
        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap ${colorClass}`}>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${colorClass.includes('text-emerald') ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            {label}
          </span>
        );
      }
    },
    */
    {
      header: "Notes",
      key: "lastActivityNote",
      className: "min-w-[250px]",
      render: (row) => {
        const l = row.leadId || {};
        let lastNote = row.notes || l.lastActivityNote || l.notes || "No notes yet";
        if (lastNote && typeof lastNote === 'string' && lastNote.includes(' | Note: ')) {
          lastNote = lastNote.split(' | Note: ')[1];
        }
        return (
          <div className={`${TableStyles.noteText} truncate max-w-[250px] inline-block`} title={lastNote}>
            {lastNote}
          </div>
        );
      }
    },
    {
      header: "Created Date",
      key: "createdAt",
      minWidth: "100px",
      render: (row) => (
        <span className="text-[11px] text-slate-500 whitespace-nowrap">
          {formatDateStr(row.createdAt)}
        </span>
      )
    },
    {
      header: "App Schedule Date",
      key: "appointmentDate",
      minWidth: "120px",
      render: (row) => {
        const l = row.leadId || {};
        const dateStr = l.appointmentDate ? new Date(l.appointmentDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—";
        return (
          <span className="text-[11px] text-slate-500 truncate block max-w-[120px]" title={dateStr}>
            {dateStr}
          </span>
        );
      }
    },
    {
      header: "Actions",
      key: "actions",
      className: "text-center w-32",
      render: (row) => {
        const l = row.leadId || {};
        const status = l.leadStatus || row.projectStatus || row.status || 'new';
        
        let onAddAction = null;
        let addTitle = "Add / Start";
        
        if (status === 'initial_quotation') {
           onAddAction = () => onAdd && onAdd(row, 'initial_quotation');
           addTitle = "Start Initial Quotation";
        } else if (status === 'budget') {
           onAddAction = () => onAdd && onAdd(row, 'budget');
           addTitle = "Start Final Quotation";
        } else if (status === 'order_placing') {
           onAddAction = () => onAdd && onAdd(row, 'order_placing');
           addTitle = "Start Ordering";
        }
        const userRole = user?.role?.toLowerCase() || '';
        const isDesigner = ['designer', 'senior_designer'].includes(userRole) || userRole === 'site_executive';
        const isAdminOrOwner = ['admin', 'owner', 'systemadmin'].includes(userRole);
        const isCompleted = status.toLowerCase() === 'completed';
        const canEdit = !(isCompleted && isDesigner && !isAdminOrOwner);

        return (
          <div onClick={(e) => e.stopPropagation()} className="flex items-center justify-start gap-1 w-[120px] mx-auto pl-1">
            <ErrorBoundary>
              <TableActions
                onAdd={!l.isDisabled ? onAddAction : undefined}
                addTitle={addTitle}
                onView={!l.isDisabled ? () => onView && onView(row) : undefined} 
                onEdit={(!l.isDisabled && canEdit) ? () => onEdit && onEdit(row) : undefined}
                onToggle={() => onDisable && onDisable(row)}
                isActive={!l.isDisabled}
                disabled={false} 
              />
            </ErrorBoundary>
          </div>
        );
      }
    }
  ];
};
